import { type CountryCode } from '@constants'
import { useApiCampaignAvailableCoupons } from '@hooks-api/fortune/useApiCampaignAvailableCoupons'
import { useApiCampaignProductsByProjectCode } from '@hooks-api/fortune-fastpass/use-api-campaign-products-byprojectcode'
import { useApiCampaignProductsList } from '@hooks-api/fortune-fastpass/use-api-campaign-products-list'
import { type Coupon, type CouponType } from '@hooks-api/types/dataModel/coupon'
import { useCallback, useMemo } from 'react'
import * as couponService from '../core/couponService'
import { parseAppliedCouponCriterionsMap } from '../parser/parseAppliedCouponCriterionsMap'
import { parseAvailableCouponCriterionGiveawayProducts } from '../parser/parseAvailableCouponCriterionGiveawayProducts'
import { parseMergedCouponMap } from '../parser/parseMergedCouponMap'
import { parseProductMap } from '../parser/parseProductMap'
import { useFpCampaignCartCalculate } from './useFpCampaignCartCalculate'
import { useFpCampaignCartSelector } from './useFpCampaignCartStore'
import { useFpCampaignProductsService } from './useFpCampaignProductsService'

export const useFpCampaignCoupons = (campaignId: number | null, countryCode: CountryCode, campaignCode: string | undefined) => {
  const manualInputCouponIds = useFpCampaignCartSelector(campaignId, state => state.coupon.manualInputCouponIds)
  const { projectCode } = useFpCampaignProductsService(campaignCode)
  const { destructuredData: availableCoupons, isLoading: isLoadingAvailableCoupons } = useApiCampaignAvailableCoupons({
    campaign_code: campaignCode,
    manual_input_coupon_ids: manualInputCouponIds.join(','),
  })
  const { destructuredData: campaignProducts } = useApiCampaignProductsList(projectCode
    ? {
      campaign_code: campaignCode,
      project_code: projectCode,
    }
    : undefined)
  const { destructuredData: calculateResult } = useFpCampaignCartCalculate(campaignId, campaignCode)
  const giveawayProducts = useMemo(() => parseAvailableCouponCriterionGiveawayProducts(availableCoupons).map(product => ({ project_code: product.project_code, product_id: product.id })), [availableCoupons])
  const allProductsWithCampaign = projectCode ? campaignProducts?.products.flatMap(product => product.all_product).map(product => ({ project_code: projectCode, product_id: product.id })) ?? [] : []
  const { destructuredData: products, isLoading: isLoadingProducts } = useApiCampaignProductsByProjectCode({
    country_code: countryCode,
    campaign_code: campaignCode,
    products: [...allProductsWithCampaign, ...giveawayProducts],
  })

  const productMap = useMemo(() => parseProductMap(products), [products])

  const appliedCouponCriterionsMap = useMemo(() => parseAppliedCouponCriterionsMap(calculateResult?.applied_coupons),
    [calculateResult])

  const mergedCouponMap = useMemo(() => parseMergedCouponMap(availableCoupons, appliedCouponCriterionsMap, productMap),
    [appliedCouponCriterionsMap, availableCoupons, productMap])

  const getMergedCouponCriterions = useCallback((couponIds: Coupon['id'][], types?: CouponType[]) => {
    if (!mergedCouponMap) return []
    return couponService.getMergedCouponCriterions(mergedCouponMap, couponIds, types)
  }, [mergedCouponMap])

  const availableMergedCoupons = useMemo(() => mergedCouponMap ? Array.from(mergedCouponMap.values()) : undefined, [mergedCouponMap])

  return {
    availableMergedCoupons,
    getMergedCouponCriterions,
    calculateResult,
    isLoadingProducts,
    isLoadingAvailableCoupons,
  }
}
